import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const container = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

export const AboutPage = () => {
  return (
    <main className="min-h-screen pt-24 bg-background px-6 py-16 max-w-6xl mx-auto text-foreground">
      {/* SEO Meta */}
      <title>About — The Blind Date</title>
      <meta
        name="description"
        content="Learn about The Blind Date, our mission, our values, and how we create meaningful offline connections."
      />

      <motion.div
        className="w-full"
        variants={container}
        initial="hidden"
        animate="visible"
      >
        {/* Breadcrumb */}
        <motion.p variants={item} className="text-sm text-foreground/60 mb-6">
          <Link to="/" className="hover:text-primary">
            Home
          </Link>{" "}
          / About
        </motion.p>

        {/* HERO TITLE */}
        <motion.h1
          variants={item}
          className="text-6xl sm:text-7xl font-light italic text-primary mb-10"
        >
          About Us
        </motion.h1>

        {/* INTRO SECTION */}
        <motion.p
          variants={item}
          className="text-xl text-foreground/70 max-w-3xl leading-relaxed mb-14"
        >
          The Blind Date was created with a simple belief: real connections
          don’t start with endless swiping, they start with real conversations.
          We’re here to bring back authenticity in the world of modern dating.
        </motion.p>

        {/* OUR STORY */}
        <motion.h2
          variants={item}
          className="text-3xl font-semibold text-primary mb-4"
        >
          How It Started
        </motion.h2>

        <motion.p
          variants={item}
          className="text-foreground/70 leading-relaxed mb-12 max-w-4xl"
        >
          In a world overloaded with dating apps, filters, and swipes, people
          were feeling more disconnected than ever. We wanted to build something
          different, something human. The Blind Date was born from the desire to
          help people experience meaningful, slow conversations instead of quick
          judgments.
        </motion.p>

        {/* OUR MISSION */}
        <motion.h2
          variants={item}
          className="text-3xl font-semibold text-primary mb-4"
        >
          Our Mission
        </motion.h2>

        <motion.p
          variants={item}
          className="text-foreground/70 leading-relaxed mb-12 max-w-4xl"
        >
          Our mission is simple: help people connect in a natural, pressure-free
          way. No algorithms deciding your fate. No endless scrolling. Just two
          people, thoughtfully matched, having a genuine conversation.
        </motion.p>

        {/* OUR VALUES */}
        <motion.h2
          variants={item}
          className="text-3xl font-semibold text-primary mb-4"
        >
          What We Believe In
        </motion.h2>

        <motion.ul
          variants={item}
          className="list-disc ml-6 space-y-3 text-foreground/70 mb-12"
        >
          <li>Authenticity over perfection</li>
          <li>Meaningful conversations over quick swipes</li>
          <li>Privacy, safety, and respect for every participant</li>
          <li>Human-driven matchmaking, not algorithms</li>
        </motion.ul>

        {/* HOW IT WORKS (MINI) */}
        <motion.h2
          variants={item}
          className="text-3xl font-semibold text-primary mb-4"
        >
          How We Match You
        </motion.h2>

        <motion.p
          variants={item}
          className="text-foreground/70 leading-relaxed mb-12 max-w-4xl"
        >
          After you complete our questionnaire, our team personally reviews your
          responses to understand your personality, values, energy, and vibe. We
          then match you with someone who aligns naturally, not just someone who
          shares your interests on paper.
        </motion.p>

        {/* CTA */}
        <motion.div variants={item} className="mt-16">
          <Link to={"/city-selection"}>
            <button className="px-12 py-4 bg-primary text-primary-foreground rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300">
              Begin Your Journey
            </button>
          </Link>
        </motion.div>
      </motion.div>
    </main>
  );
};
